import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, FileText, CheckCircle, AlertCircle, LogOut } from 'lucide-react';
import { useAuth } from '@hooks/useAuth';
import { useDocument } from '@hooks/useDocument';
import { ROUTES, DOCUMENT_STATUS } from '@constants';
import Card from '@components/common/Card';
import Button from '@components/common/Button';
import { useState } from 'react';
import toast from 'react-hot-toast';

const ProfileUnderVerification = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { getMyDocuments } = useDocument();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role !== 'mentor') {
      navigate(ROUTES.DASHBOARD);
      return;
    }

    fetchDocuments();
  }, [user, navigate]);

  const fetchDocuments = async () => {
    try {
      const response = await getMyDocuments();
      if (response.success) {
        const docs = response.data?.data || response.data || [];
        setDocuments(docs);
        
        // If no documents uploaded, redirect to complete profile page
        if (docs.length === 0) {
          navigate(ROUTES.COMPLETE_PROFILE);
          return;
        }
      }
    } catch (error) {
      console.error('Failed to fetch documents', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate(ROUTES.LOGIN);
    } catch (error) {
      toast.error('Failed to logout');
    }
  };

  const getDocumentLabel = (type) => {
    const labels = {
      aadhar: 'Aadhar Card',
      degree_certificate: 'Degree Certificate',
      experience_certificate: 'Experience Certificate',
    };
    return labels[type] || type;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case DOCUMENT_STATUS.APPROVED:
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case DOCUMENT_STATUS.REJECTED:
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      case DOCUMENT_STATUS.PENDING:
        return <Clock className="w-5 h-5 text-yellow-600" />;
      default:
        return null;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case DOCUMENT_STATUS.APPROVED:
        return 'Approved';
      case DOCUMENT_STATUS.REJECTED:
        return 'Rejected';
      case DOCUMENT_STATUS.PENDING:
        return 'Under Review';
      default:
        return 'Unknown';
    }
  };

  const allApproved = documents.length === 3 && documents.every(doc => doc.status === DOCUMENT_STATUS.APPROVED);
  const anyRejected = documents.some(doc => doc.status === DOCUMENT_STATUS.REJECTED);
  const anyPending = documents.some(doc => doc.status === DOCUMENT_STATUS.PENDING);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Blurred Background Overlay */}
      <div className="fixed inset-0 bg-primary-900/80 backdrop-blur-md" />
      
      {/* Modal Content */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          {/* Status Icon Badge */}
          <div className="absolute -top-12 left-1/2 transform -translate-x-1/2">
            <div className={`rounded-full p-4 shadow-2xl ${
              allApproved 
                ? 'bg-gradient-to-br from-green-600 to-green-700' 
                : anyRejected 
                ? 'bg-gradient-to-br from-red-600 to-red-700'
                : 'bg-gradient-to-br from-yellow-600 to-yellow-700'
            }`}>
              {allApproved ? (
                <CheckCircle className="w-8 h-8 text-white" />
              ) : anyRejected ? (
                <AlertCircle className="w-8 h-8 text-white" />
              ) : (
                <Clock className="w-8 h-8 text-white" />
              )}
            </div>
          </div>

          <Card className="bg-white shadow-2xl border border-primary-200 p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-primary-700 mb-2">
                {allApproved ? 'Profile Approved!' : anyRejected ? 'Action Required' : 'Profile Under Verification'}
              </h1>
              
              <p className="text-primary-600">
                {allApproved
                  ? 'Your profile has been approved. You can now access the dashboard.'
                  : anyRejected
                  ? 'Some documents were rejected. Please reupload them.'
                  : 'Your documents are being reviewed by our admin team. This usually takes 24-48 hours.'}
              </p>
            </div>

          <div className="space-y-4 mb-8">
            <h2 className="text-lg font-semibold text-primary-700 mb-4">
              Document Status
            </h2>
            
            {documents.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center justify-between p-4 bg-primary-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-primary-600" />
                  <div>
                    <p className="font-medium text-primary-700">
                      {getDocumentLabel(doc.documentType)}
                    </p>
                    {doc.status === DOCUMENT_STATUS.REJECTED && doc.rejectionReason && (
                      <p className="text-sm text-red-600 mt-1">
                        Reason: {doc.rejectionReason}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusIcon(doc.status)}
                  <span className="text-sm font-medium text-primary-600">
                    {getStatusText(doc.status)}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-3">
            {anyRejected && (
              <Button
                onClick={() => navigate(ROUTES.COMPLETE_PROFILE)}
                variant="primary"
                fullWidth
              >
                Reupload Rejected Documents
              </Button>
            )}
            
            {anyPending && !anyRejected && (
              <Button
                onClick={() => navigate(ROUTES.COMPLETE_PROFILE)}
                variant="primary"
                fullWidth
              >
                Change Documents Under Review
              </Button>
            )}
            
            {allApproved && (
              <Button
                onClick={() => navigate(ROUTES.DASHBOARD)}
                variant="primary"
                fullWidth
              >
                Go to Dashboard
              </Button>
            )}
            
            <Button
              onClick={() => fetchDocuments()}
              variant="outline"
              fullWidth
            >
              Refresh Status
            </Button>
            
            <Button
              onClick={handleLogout}
              variant="outline"
              fullWidth
              className="border-red-300 text-red-600 hover:bg-red-50"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>

          {!allApproved && !anyRejected && (
            <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> You'll receive an email notification once your documents are reviewed. 
                You can also check back here anytime to see the status. If you need to change any document before approval, click the button above.
              </p>
            </div>
          )}

          {/* Cannot Skip Message */}
          <div className="mt-6 text-center">
            <p className="text-sm text-primary-500 flex items-center justify-center gap-2">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              {allApproved ? 'Profile verified - Access granted' : 'Verification required to access platform'}
            </p>
          </div>
        </Card>
        </div>
      </div>
    </div>
  );
};

export default ProfileUnderVerification;
